import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, X, FileText, AlertCircle } from "lucide-react";
import { useTypstCompiler } from "@/hooks/useTypstCompiler";
import { PDFViewer } from "@/components/document-writer/PDFViewer";

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUseTemplate: () => void;
  template: {
    _id: string;
    name: string;
    description?: string;
    typstContent: string;
  } | null;
}

/**
 * Modal for previewing a template as a rendered PDF
 * Compiles the template's Typst content and displays it
 */
export function TemplatePreviewModal({
  isOpen,
  onClose,
  onUseTemplate,
  template,
}: TemplatePreviewModalProps) {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [compileError, setCompileError] = useState<string | null>(null);
  const { isReady: isCompilerReady, compile, isCompiling } = useTypstCompiler();

  // Compile template when modal opens
  useEffect(() => {
    if (!isOpen || !template?.typstContent || !isCompilerReady) {
      return;
    }

    const compileTemplate = async () => {
      try {
        setCompileError(null);
        setPdfBlob(null);

        const blob = await compile(template.typstContent);
        if (blob) {
          setPdfBlob(blob);
        } else {
          setCompileError("Failed to compile template");
        }
      } catch (error) {
        console.error("Template compilation error:", error);
        setCompileError(error instanceof Error ? error.message : "Compilation failed");
      }
    };

    compileTemplate();
  }, [isOpen, template?.typstContent, isCompilerReady, compile]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPdfBlob(null);
      setCompileError(null);
    }
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  if (!template) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="flex h-[85vh] max-w-4xl flex-col p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-purple-100 p-2">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <DialogTitle className="text-lg">{template.name}</DialogTitle>
                {template.description && (
                  <p className="mt-0.5 text-sm text-gray-500">{template.description}</p>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* PDF Preview Area */}
        <div className="flex-1 overflow-auto bg-gray-100">
          {!isCompilerReady || isCompiling ? (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              <p className="text-sm text-gray-600">
                {!isCompilerReady ? "Loading compiler..." : "Compiling template..."}
              </p>
            </div>
          ) : compileError ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-8">
              <div className="rounded-full bg-red-100 p-3">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <p className="text-center text-sm text-gray-600">
                Unable to preview this template. The template may contain syntax errors.
              </p>
              <p className="max-w-md text-center text-xs text-gray-400">{compileError}</p>
            </div>
          ) : pdfBlob ? (
            <div className="p-4">
              <PDFViewer pdfBlob={pdfBlob} className="w-full" />
            </div>
          ) : null}
        </div>

        {/* Footer with Actions */}
        <div className="flex shrink-0 items-center justify-between border-t bg-white px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            onClick={() => {
              onClose();
              onUseTemplate();
            }}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
          >
            <Sparkles className="h-4 w-4" />
            Use This Template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
